import { useState } from "react";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { createFileRoute } from "@tanstack/react-router";

import { parseSEOMetaLoaderData, seo } from "@/lib/seo";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import FilterNavigation from "@/components/faq/filter-navigation";
import Questions from "@/components/faq/questions";

import Logo from "@/assets/icons/logo.svg?react";

export const Route = createFileRoute("/_default-layout/faq")({
  loader: ({ context: { i18n }, location }) => {
    return {
      seoMeta: seo({
        i18n,
        path: location.pathname,
        title: msg`FAQ - Frequently Asked Questions`,
        description: msg`Find answers to frequently asked questions about El Safwa's properties, services, and policies. Get information for both guests and property owners.`,
        keywords:
          "El Safwa FAQ, frequently asked questions, property information, guest policies, owner policies, El Safwa help",
      }),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      ...parseSEOMetaLoaderData(loaderData, {
        title: "FAQ",
      }),
    ],
  }),
  component: RouteComponent,
});

const faqTypes = [
  { label: msg`For Guests`, value: "guests" },
  { label: msg`For Property Owners`, value: "owners" },
] as const;

function RouteComponent() {
  const { i18n } = useLingui();
  const [selectedType, setSelectedType] = useState<"guests" | "owners">("guests");
  const [selectedCategory, setSelectedCategory] = useState("general");

  return (
    <div className="container my-12">
      <div className="flex flex-wrap md:flex-nowrap md:justify-between">
        <div className="w-full md:basis-1/3 md:pe-10">
          <div className="mb-4 rounded-4xl bg-transparent md:bg-(--color-bg-color-bg-light-beige) md:p-5">
            <div className="hidden w-full justify-end md:flex">
              <Logo className="size-[46px]" />
            </div>
            <h1 className="mb-4 text-3xl md:hidden">
              <Trans>
                Frequently Asked <span className="font-semibold">Questions</span>
              </Trans>
            </h1>
            <div>
              <p className="text-base leading-6 font-semibold md:text-xl md:leading-7">
                <Trans>Filter by</Trans>
              </p>
            </div>
          </div>

          <div className="mb-8 rounded-4xl md:my-auto md:border md:px-2.5 md:py-5">
            <FilterNavigation
              type={selectedType}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
            />
          </div>
        </div>

        <div className="w-full md:basis-2/3">
          <h1 className="mb-4 hidden text-5xl leading-16 md:block">
            <Trans>
              Frequently Asked <span className="font-semibold">Questions</span>
            </Trans>
          </h1>
          <Tabs className="w-full rtl:text-right" defaultValue="guests" value={selectedType}>
            <div className="border-[rgba(59, 66, 48, 0.20)] mb-4 border-b pb-2.5">
              <TabsList>
                {faqTypes.map(({ label, value }) => (
                  <TabsTrigger
                    key={value}
                    value={value}
                    onClick={() => {
                      setSelectedType(value);
                      setSelectedCategory("general");
                    }}
                    className="bg-transparent data-[state=active]:bg-[#D1F27A]"
                  >
                    {i18n._(label)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            {faqTypes.map(({ value }, i) => (
              <TabsContent key={i} value={value} className="space-y-5">
                <Questions type={value} category={selectedCategory} />
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </div>
  );
}
